/**
 * faction-affinity.ts — 門派風味快照 (Phase 2, §4-0 / §5.4)。
 *
 * 鐵則(學習與遊戲分離 — 唯一硬禁止):本檔 **絕不進任何傷害/攻速/HP 公式**。
 * 它讀玩家修煉值 (skillLevels / factionXP) 算出一份「門派親和度」唯讀快照
 * (每門 0..1),唯一用途是給 `flavorPool()` 當「哪張卡更常出現」的權重來源。
 * 同一張卡無論玩家練哪一門,`apply()` 寫進 RunModifiers 的數字一模一樣 —
 * affinity 只改「出現機率」,不改「效果大小」。
 *
 * 為何 Phaser-free:守 §6 邊界,可純單元測試,且零誘惑接到戰鬥數值或持久化。
 * scene 進場呼叫一次 → 唯讀快照 → 整場不變。
 */

import type { FactionId, PlayerProgress } from '../../shared/types';
import { SKILL_CATALOG } from '../../shared/skills';

/** 五門派 → 0..1 的親和度權重快照。 */
export type FactionAffinity = Record<FactionId, number>;

/** 全門派列表 (與 types.ts FactionId union 對齊)。 */
const FACTION_IDS: FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'];

/**
 * 未練門派的「保底權重」。鐵則安全:不歸零,讓任何門派的風味卡都還抽得到
 * (只是機率低),確保池永遠多元、池 ≥3 可抽。
 */
export const AFFINITY_FLOOR = 0.2;

/**
 * 計算門派親和度快照。
 *
 * 演算法 (純資料,可重現):
 *   1. 每門派的「投入分數」= 該門派所有技能的 skillLevels 總和 × 權重
 *      + factionXP 的對數貢獻 (XP 大但邊際遞減,避免單一門派 XP 暴衝壓死其他門)。
 *   2. 正規化到 0..1 (除以最大投入分數),最高投入門 = 1.0。
 *   3. 套保底 AFFINITY_FLOOR:未練/低投入門派至少 0.2 (不歸零)。
 *
 * 完全沒投入 (全新存檔) → 每門皆 = AFFINITY_FLOOR (均勻,風味中性)。
 *
 * @param progress 玩家進度 (唯讀;本函式不修改、不寫回)。
 */
export function computeFactionAffinity(progress: PlayerProgress): FactionAffinity {
  // skill id → faction 對照 (builtin 技能不歸任何門派,跳過)。
  const skillFaction = new Map<string, FactionId>();
  for (const s of SKILL_CATALOG) {
    if (s.faction !== 'builtin') skillFaction.set(s.id, s.faction);
  }

  const raw: Record<FactionId, number> = {
    celestial: 0, runic: 0, techno: 0, elemental: 0, dominion: 0,
  };

  // (1a) 技能等級投入 (主訊號:玩家實際練了哪門技能)。
  const skillLevels = progress.skillLevels ?? {};
  for (const [id, lvl] of Object.entries(skillLevels)) {
    const f = skillFaction.get(id);
    if (!f) continue;
    if (Number.isFinite(lvl) && lvl > 0) raw[f] += lvl;
  }

  // (1b) factionXP 對數貢獻 (次訊號:邊際遞減,避免壓死其他門)。
  const factionXP = progress.factionXP ?? ({} as Record<FactionId, number>);
  for (const f of FACTION_IDS) {
    const xp = factionXP[f];
    if (Number.isFinite(xp) && xp > 0) raw[f] += Math.log10(xp + 1);
  }

  // (2) 正規化到 0..1。
  let max = 0;
  for (const f of FACTION_IDS) if (raw[f] > max) max = raw[f];

  const out: FactionAffinity = {
    celestial: AFFINITY_FLOOR, runic: AFFINITY_FLOOR, techno: AFFINITY_FLOOR,
    elemental: AFFINITY_FLOOR, dominion: AFFINITY_FLOOR,
  };
  if (max <= 0) return out; // 全新存檔:均勻保底。

  // (3) 正規化 + 保底 (未練門不歸零)。
  for (const f of FACTION_IDS) {
    const norm = raw[f] / max; // 0..1
    out[f] = AFFINITY_FLOOR + (1 - AFFINITY_FLOOR) * norm;
  }
  return out;
}
